import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ShoppingBag, Heart, ArrowLeft, Check, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { productImage } from "@/lib/images";

export default function ProductDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => nav("/shop"));
  }, [id, nav]);

  if (!product) {
    return (
      <div className="container-x py-32 text-center text-[#5C4D43]" data-testid="product-loading">
        Loading...
      </div>
    );
  }

  const handleAdd = () => {
    setAdding(true);
    addItem(product, {}, qty);
    toast.success(`${product.name} added to your basket`);
    setTimeout(() => setAdding(false), 600);
  };

  return (
    <div className="container-x py-12 lg:py-16" data-testid="product-detail">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#5C4D43] hover:text-[#D97757]" data-testid="back-to-shop">
        <ArrowLeft size={16} /> Back to the family
      </Link>

      <div className="mt-8 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="rounded-3xl overflow-hidden bg-[#F4EFE6] aspect-square">
            <img src={productImage(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#5C4D43]">
            {product.category}{product.size ? ` · ${product.size}` : ""}
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl tracking-tight mt-2" data-testid="product-name">
            {product.name}
          </h1>
          <div className="font-serif text-3xl text-[#D97757] mt-4" data-testid="product-price">
            £{product.price.toFixed(2)}
          </div>
          <p className="mt-6 text-[#5C4D43] leading-relaxed">{product.description}</p>

          <div className="mt-6 p-5 rounded-2xl bg-[#F4EFE6]/70 border border-[#EAE3D9]" data-testid="safety-note">
            <div className="font-medium text-[#2C211B] flex items-center gap-2">
              <span className="text-[#A3B18A]">●</span> Made to UK toy safety standards
            </div>
            <p className="mt-1 text-sm text-[#5C4D43] leading-relaxed">
              Compliant with the <strong>Toys (Safety) Regulations 2011</strong> and
              relevant <strong>BS EN 71</strong> requirements. Safety eyes are
              washer-locked and pull-tested. <span className="font-medium">
              ⚠ Not suitable for children under 36 months</span> — small parts,
              choking hazard. <span className="font-medium">⚠ Filled with hi-loft
              polyester — keep away from fire and open flame.</span> Adult
              supervision is always recommended.{" "}
              <Link to="/safety" className="underline hover:text-[#D97757]">
                Read our full safety statement →
              </Link>
            </p>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-3 border border-[#EAE3D9] rounded-full px-2 py-1">
              <button
                data-testid="qty-decrement"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-9 h-9 rounded-full hover:bg-[#F4EFE6]"
              >–</button>
              <span data-testid="qty-value" className="w-6 text-center">{qty}</span>
              <button
                data-testid="qty-increment"
                onClick={() => setQty((q) => q + 1)}
                className="w-9 h-9 rounded-full hover:bg-[#F4EFE6]"
              >+</button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              className="btn-primary inline-flex items-center gap-2 flex-1 justify-center"
            >
              {adding ? <Check size={18} /> : <ShoppingBag size={18} />}
              {adding ? "Added!" : "Add to basket"}
            </button>
          </div>

          <Link
            to="/personalise"
            data-testid="personalise-cta"
            className="mt-5 flex items-start gap-4 p-5 rounded-2xl border border-[#EAE3D9] bg-white hover:border-[#D97757] transition-all group"
          >
            <Sparkles size={20} className="text-[#D97757] shrink-0 mt-0.5" />
            <div>
              <div className="font-serif text-lg text-[#2C211B] group-hover:text-[#D97757]">
                Want it personalised?
              </div>
              <div className="text-sm text-[#5C4D43] mt-0.5">
                Order a one-of-a-kind version — choose the animal, colour, name and birthday on our personalisation page.
              </div>
            </div>
          </Link>

          <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
            <div className="card-soft p-4">
              <Heart size={18} className="text-[#D97757] mb-2" />
              <div className="font-medium">Cotton & love</div>
              <div className="text-xs text-[#5C4D43] mt-1">100% cotton yarn</div>
            </div>
            <div className="card-soft p-4">
              <div className="text-[#D97757] mb-2 font-serif text-lg leading-none">48h</div>
              <div className="font-medium">Crafted slowly</div>
              <div className="text-xs text-[#5C4D43] mt-1">Avg. per piece</div>
            </div>
            <div className="card-soft p-4">
              <div className="text-[#D97757] mb-2 font-serif text-lg leading-none">UK</div>
              <div className="font-medium">Royal Mail 2nd</div>
              <div className="text-xs text-[#5C4D43] mt-1">UK only · timing varies with demand</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
